import type { ClassActivity } from '@/types/activity';

// Grading moved to `@/constants/grading` once exams needed it too. Re-exported
// here so the activity screens keep importing from one place.
export { gradeFor, parseScore, type GradeBand } from '@/constants/grading';

/**
 * The small sums both activity screens do.
 *
 * Written once because the card and the review header show the same numbers,
 * and two copies would eventually round differently (CLAUDE.md §3).
 */

/**
 * How much of the class has handed something in, 0-100.
 *
 * A section with nobody on its roster yet is 0 rather than `NaN` — which is
 * what a bare division gives, and what would reach the progress bar's width.
 */
export function submissionPercent(activity: Pick<ClassActivity, 'submissionCount' | 'totalStudents'>): number {
  if (activity.totalStudents <= 0) return 0;
  return Math.round((activity.submissionCount / activity.totalStudents) * 100);
}

/** "18/20", or "Not scored" for an activity with no marks against it. */
export function describeScore(score: number | null, maxPoints: number | null): string {
  if (maxPoints === null) return 'Not scored';
  if (score === null) return `— / ${maxPoints}`;
  return `${score} / ${maxPoints}`;
}

/** "5 marked of 32 handed in" — the one line the review header leads with. */
export function describeProgress(activity: Pick<ClassActivity, 'submissionCount' | 'gradedCount' | 'totalStudents'>): string {
  return `${activity.gradedCount} marked · ${activity.submissionCount} of ${activity.totalStudents} handed in`;
}

/** `1048576` → `1.0 MB`. Null size is an upload from before we recorded it. */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes <= 0) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
